import React, { useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useDispatch, useSelector } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { getClockStatus } from '@/store/slices/timeClockSlice';
import { getEmployeeProfile } from '@/store/slices/employeeSlice';
import { getUnreadCount } from '@/store/slices/notificationsSlice';

const DashboardScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { clockStatus, isLoading: clockLoading } = useSelector((state: RootState) => state.timeClock);
  const { currentEmployee, isLoading: employeeLoading } = useSelector((state: RootState) => state.employee);

  const isLoading = clockLoading || employeeLoading;
  const isAdmin = !!user?.employee?.admin;

  const unreadCount = useSelector((state: RootState) => state.notifications?.unreadCount ?? 0);

  const onRefresh = useCallback(() => {
    dispatch(getUnreadCount());
    if (user?.employee?.id) {
      dispatch(getClockStatus(user.employee.id));
      dispatch(getEmployeeProfile(user.employee.id));
    }
  }, [dispatch, user?.employee?.id]);

  // Refetch on focus (not just mount) — tab screens stay mounted, so clock
  // status + recent activity would otherwise go stale after a clock in/out.
  useFocusEffect(onRefresh);

  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getClockStatusText = () => {
    if (!clockStatus) return 'Unknown';
    return clockStatus.clockedIn ? 'Clocked In' : 'Clocked Out';
  };

  const getClockStatusColor = () => {
    if (!clockStatus) return '#999999';
    return clockStatus.clockedIn ? '#34C759' : '#FF3B30';
  };

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={isLoading} onRefresh={onRefresh} />}>
      
      {/* Welcome Section */}
      <View style={styles.welcomeSection}>
        <Text style={styles.welcomeText}>Welcome back,</Text>
        <Text style={styles.nameText}>{user?.employee?.firstName}!</Text>
        {currentEmployee?.department && (
          <Text style={styles.departmentText}>{currentEmployee.department.name}</Text>
        )}
        <TouchableOpacity
          style={styles.bell}
          onPress={() => navigation.navigate('Notifications')}
          accessibilityLabel="Notifications">
          <Icon name="notifications-outline" size={26} color="#333333" />
          {unreadCount > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Quick Actions */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.actionsGrid}>
          <TouchableOpacity
            style={styles.actionCard}
            onPress={() =>
              navigation.navigate('TimeClock', { screen: 'TimeClockHome', params: { mode: 'today' } })
            }>
            <Icon name="time-outline" size={32} color="#007AFF" />
            <Text style={styles.actionText}>Time Clock</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionCard}
            onPress={() => navigation.navigate('Schedule')}>
            <Icon name="calendar-outline" size={32} color="#007AFF" />
            <Text style={styles.actionText}>Schedule</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionCard}
            onPress={() =>
              navigation.navigate('TimeClock', { screen: 'TimeClockHome', params: { mode: 'pay' } })
            }>
            <Icon name="document-text-outline" size={32} color="#007AFF" />
            <Text style={styles.actionText}>Timesheet</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionCard}
            onPress={() => navigation.navigate('Profile')}>
            <Icon name="person-outline" size={32} color="#007AFF" />
            <Text style={styles.actionText}>Profile</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionCard}
            onPress={() => navigation.navigate('Messages', { screen: 'Directory' })}>
            <Icon name="people-outline" size={32} color="#007AFF" />
            <Text style={styles.actionText}>Directory</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionCard}
            onPress={() => navigation.navigate('CallPerformance')}>
            <Icon name="call-outline" size={32} color="#007AFF" />
            <Text style={styles.actionText}>My Calls</Text>
          </TouchableOpacity>

          {isAdmin && (
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('UnassignedCalls')}>
              <Icon name="git-branch-outline" size={32} color="#007AFF" />
              <Text style={styles.actionText}>Unassigned Calls</Text>
            </TouchableOpacity>
          )}

          {isAdmin && (
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('Schedule', { screen: 'Approvals' })}>
              <Icon name="checkmark-done-outline" size={32} color="#007AFF" />
              <Text style={styles.actionText}>Approvals</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Clock Status */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Time Clock Status</Text>
        <View style={styles.statusCard}>
          <View style={styles.statusHeader}>
            <View style={[styles.statusIndicator, { backgroundColor: getClockStatusColor() }]} />
            <Text style={styles.statusText}>{getClockStatusText()}</Text>
          </View>
          
          {clockStatus?.clockedIn && clockStatus.clockInTime && (
            <View style={styles.statusDetails}>
              <Text style={styles.statusLabel}>Clocked in at:</Text>
              <Text style={styles.statusValue}>
                {new Date(clockStatus.clockInTime).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
              <Text style={styles.statusLabel}>Time worked:</Text>
              <Text style={styles.statusValue}>
                {formatMinutes(clockStatus.minutesWorked)}
              </Text>
            </View>
          )}
          
          {clockStatus?.lastPunch && !clockStatus.clockedIn && (
            <View style={styles.statusDetails}>
              <Text style={styles.statusLabel}>Last punch:</Text>
              <Text style={styles.statusValue}>
                {new Date(clockStatus.lastPunch.startTime).toLocaleDateString()} at{' '}
                {new Date(clockStatus.lastPunch.startTime).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Recent Activity */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        <View style={styles.activityCard}>
          {(clockStatus?.recentPunches?.length ?? 0) === 0 ? (
            <Text style={styles.activityText}>No recent activity</Text>
          ) : (
            clockStatus!.recentPunches!.slice(0, 6).map((p, i, arr) => {
              const start = new Date(p.startTime);
              const open = !p.endTime;
              const inTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const dateLabel = start.toLocaleDateString([], { month: 'short', day: 'numeric' });
              const outTime = p.endTime
                ? new Date(p.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : null;
              return (
                <View
                  key={p.id}
                  style={[styles.activityRow, i === arr.length - 1 && styles.activityRowLast]}>
                  <Icon
                    name={open ? 'play-circle' : 'checkmark-circle'}
                    size={20}
                    color={open ? '#EB4A26' : '#1F5E3C'}
                  />
                  <View style={styles.activityRowBody}>
                    <Text style={styles.activityRowTitle}>
                      {open ? 'Clocked in' : `Worked ${formatMinutes(p.duration)}`}
                    </Text>
                    <Text style={styles.activityRowMeta}>
                      {dateLabel} · {inTime}
                      {outTime ? ` – ${outTime}` : ' · on the clock'}
                    </Text>
                  </View>
                </View>
              );
            })
          )}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F8F8',
  },
  welcomeSection: {
    backgroundColor: '#FFFFFF',
    padding: 24,
    marginBottom: 16,
  },
  bell: {
    position: 'absolute',
    top: 24,
    right: 24,
    padding: 4,
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#FF3B30',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
  },
  welcomeText: {
    fontSize: 18,
    color: '#666666',
  },
  nameText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 4,
  },
  departmentText: {
    fontSize: 16,
    color: '#007AFF',
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginBottom: 16,
    padding: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 16,
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  actionCard: {
    width: '48%',
    aspectRatio: 1,
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  actionText: {
    fontSize: 14,
    color: '#333333',
    marginTop: 8,
    textAlign: 'center',
  },
  statusCard: {
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    padding: 16,
  },
  statusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  statusText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333333',
  },
  statusDetails: {
    borderTopWidth: 1,
    borderTopColor: '#E5E5EA',
    paddingTop: 12,
  },
  statusLabel: {
    fontSize: 14,
    color: '#666666',
    marginBottom: 4,
  },
  statusValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333333',
    marginBottom: 8,
  },
  activityCard: {
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 4,
    minHeight: 60,
    justifyContent: 'center',
  },
  activityText: {
    fontSize: 16,
    color: '#666666',
    textAlign: 'center',
    paddingVertical: 20,
  },
  activityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#ECECEC',
  },
  activityRowLast: {
    borderBottomWidth: 0,
  },
  activityRowBody: {
    flex: 1,
  },
  activityRowTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333333',
  },
  activityRowMeta: {
    fontSize: 13,
    color: '#888888',
    marginTop: 2,
  },
});

export default DashboardScreen;